import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, Edit2, X, ChevronLeft, ChevronRight, Archive, RotateCcw, UserPlus, Pencil, Plus } from 'lucide-react';
import { api } from '../services/api';
import { exportAndDownload } from '../utils/downloadPdf';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import AddContactModal from '../components/AddContactModal';
import AssociateContactModal from '../components/AssociateContactModal';
import AgencyLogo from '../components/AgencyLogo';
import { useIsMobile } from '../hooks/useIsMobile';
import type { Agency, Contact, ProcedureStep } from '../types';

const PAGE_SIZE = 50;

const AgencyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { agingDays, overdueDays } = useSettings();
  const qc = useQueryClient();

  const [contactOffset, setContactOffset] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Agency>>({});
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAssociateContact, setShowAssociateContact] = useState(false);
  const [editingProcedures, setEditingProcedures] = useState(false);
  const [procedureSteps, setProcedureSteps] = useState<string[]>([]);

  const { data: agency, isLoading } = useQuery<Agency>({
    queryKey: ['agency', id],
    queryFn: () => api.getAgency(id!),
    enabled: !!id,
  });

  const { data: contactsData } = useQuery({
    queryKey: ['agency-contacts', id, contactOffset, showArchived],
    queryFn: () =>
      api.searchContacts({
        agency_id: id,
        limit: PAGE_SIZE,
        offset: contactOffset,
        active_only: showArchived ? 'false' : 'true',
      }),
    enabled: !!id,
    placeholderData: (prev) => prev,
  });

  const allContacts: Contact[] = contactsData?.data ?? [];
  const contacts = showArchived ? allContacts.filter((c) => !c.is_active) : allContacts;
  const contactTotal: number = showArchived ? contacts.length : (contactsData?.meta?.total ?? 0);
  const contactPages = Math.max(1, Math.ceil(contactTotal / PAGE_SIZE));
  const contactPage = Math.floor(contactOffset / PAGE_SIZE) + 1;

  const updateMutation = useMutation({
    mutationFn: () => api.updateAgency(id!, editForm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agency', id] });
      setShowEdit(false);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => api.archiveAgency(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agency', id] });
      qc.invalidateQueries({ queryKey: ['agencies'] });
      setShowArchiveConfirm(false);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => api.reactivateAgency(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agency', id] });
      qc.invalidateQueries({ queryKey: ['agencies'] });
      setShowReactivateConfirm(false);
    },
  });

  const saveProceduresMutation = useMutation({
    mutationFn: () => {
      const steps = procedureSteps
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s, i) => ({ step: i + 1, procedure: s }));
      return api.updateAgency(id!, {
        contact_procedures_structured: steps,
        contact_procedures: steps.length
          ? steps.map((s) => `${s.step}. ${s.procedure}`).join('\n')
          : null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agency', id] });
      setEditingProcedures(false);
    },
  });

  const exportAgencyPDF = async () => {
    try {
      await exportAndDownload(() => api.exportAgencyPDF(id!), `agency-${id}.pdf`);
    } catch (err: any) {
      alert(err?.message ?? 'Export failed.');
    }
  };

  const openEdit = () => {
    if (agency) setEditForm({ ...agency });
    setShowEdit(true);
  };

  const isMobile = useIsMobile();
  const canEdit = user?.role === 'Administrator' || user?.role === 'Superuser';
  const isAdminOnly = user?.role === 'Administrator';

  if (isLoading)
    return <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)' }}>Loading…</div>;
  if (!agency)
    return <div style={{ padding: 32, textAlign: 'center', color: 'var(--red)' }}>Agency not found.</div>;

  return (
    <div style={{ maxWidth: 860, paddingBottom: 40 }}>
      <button className="btn btn-ghost" style={{ marginBottom: 12 }} onClick={() => navigate(-1)}>
        <ArrowLeft size={14} />
        Back
      </button>

      {/* Header */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <AgencyLogo name={agency.name} acronym={agency.acronym ?? undefined} size={48} />
            <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 900, overflow: 'hidden', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>{agency.name}</h2>
              <span className="chip" style={{ fontWeight: 700 }}>{agency.acronym}</span>
              <span
                className="pill"
                style={{
                  background: agency.is_active ? 'var(--green-50)' : 'var(--gray-100)',
                  color: agency.is_active ? 'var(--green)' : 'var(--gray-500)',
                }}
              >
                {agency.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            {(agency.street || agency.city) && (
              <div style={{ fontSize: 14, color: 'var(--gray-500)' }}>
                {[agency.street, agency.city, agency.state, agency.postal_code]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            )}
            </div>
          </div>
          <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : undefined, gap: 8, flexWrap: 'wrap' }}>
            {isAdminOnly && agency.is_active && (
              <button
                className="btn btn-secondary"
                style={{ color: 'var(--red)', borderColor: '#fca5a5' }}
                onClick={() => setShowArchiveConfirm(true)}
              >
                <Archive size={13} />
                Archive
              </button>
            )}
            {isAdminOnly && !agency.is_active && (
              <button className="btn btn-secondary" onClick={() => setShowReactivateConfirm(true)}>
                <RotateCcw size={13} />
                Reactivate
              </button>
            )}
            {canEdit && agency.is_active && (
              <button className="btn btn-secondary" onClick={openEdit}>
                <Edit2 size={13} />
                Edit
              </button>
            )}
            {canEdit && agency.is_active && (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowAssociateContact(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <UserPlus size={13} />
                  Associate Contact
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowAddContact(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <UserPlus size={13} />
                  New Contact
                </button>
              </>
            )}
            <button className="btn btn-secondary" onClick={exportAgencyPDF}>
              <FileText size={13} />
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Agency info */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Details
        </h3>
        <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px 24px', margin: 0 }}>
          {[
            ['Website', agency.website ? (
              <a key="w" href={agency.website} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)' }}>
                {agency.website}
              </a>
            ) : '—'],
            ['Contact Count', agency.contact_count != null ? String(agency.contact_count) : '—'],
          ].map(([label, val]) => (
            <div key={label as string}>
              <dt style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                {label}
              </dt>
              <dd style={{ margin: 0, fontSize: 14 }}>{val as React.ReactNode}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Verification Health Card */}
      {(() => {
        const fresh   = (agency as any).verified_fresh   ?? 0;
        const aging   = (agency as any).verified_aging   ?? 0;
        const overdue = (agency as any).verified_overdue ?? 0;
        const never   = (agency as any).verified_never   ?? 0;
        const total   = fresh + aging + overdue + never;
        if (total === 0) return null;
        const pct = (n: number) => total > 0 ? `${Math.round((n / total) * 100)}%` : '0%';
        const segments = [
          { label: 'Fresh',   count: fresh,   color: 'var(--green)' },
          { label: 'Aging',   count: aging,   color: 'var(--amber)' },
          { label: 'Overdue', count: overdue, color: 'var(--red)' },
          { label: 'Never',   count: never,   color: 'var(--gray-500)' },
        ].filter((s) => s.count > 0);
        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Verification Health
              </h3>
              {canEdit && (
                <button
                  className="btn btn-secondary"
                  style={{ fontSize: 14 }}
                  onClick={() => navigate(`/update-requests?create=1&agency_id=${id}`)}
                >
                  Send Update Request
                </button>
              )}
            </div>
            <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', height: 12, marginBottom: 10 }}>
              {segments.map((s) => (
                <div key={s.label} style={{ width: pct(s.count), background: s.color, transition: 'width 0.3s' }} title={`${s.label}: ${s.count}`} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {segments.map((s) => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
                  <span style={{ color: 'var(--gray-600)' }}>{s.label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--gray-900)' }}>{s.count}</span>
                  <span style={{ color: 'var(--gray-500)' }}>({pct(s.count)})</span>
                </div>
              ))}
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginLeft: 'auto' }}>{total} active contacts</div>
            </div>
          </div>
        );
      })()}

      {/* Contact Procedures */}
      {(canEdit || (agency.contact_procedures_structured && agency.contact_procedures_structured.length > 0) || agency.contact_procedures) && (() => {
        const steps: ProcedureStep[] = agency.contact_procedures_structured && agency.contact_procedures_structured.length > 0
          ? agency.contact_procedures_structured
          : [];
        const hasSteps = steps.length > 0;

        const openEdit = () => {
          setProcedureSteps(hasSteps ? steps.map((s) => s.procedure) : ['']);
          setEditingProcedures(true);
        };

        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Contact Procedures
              </h3>
              {canEdit && !editingProcedures && (
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 6px' }}
                  onClick={openEdit}
                >
                  <Pencil size={12} /> Edit Procedures
                </button>
              )}
            </div>

            {editingProcedures ? (
              <div style={{ border: '1px solid var(--amber)', borderRadius: 8, background: 'var(--amber-50)', padding: 16 }}>
                {procedureSteps.map((step, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                    <div style={{ width: 28, textAlign: 'center', fontWeight: 700, color: 'var(--electric)', fontSize: 14, paddingTop: 8, flexShrink: 0 }}>
                      {idx + 1}
                    </div>
                    <textarea
                      className="field-input"
                      rows={1}
                      style={{ flex: 1, resize: 'vertical', minHeight: 34 }}
                      value={step}
                      autoFocus={idx === procedureSteps.length - 1}
                      placeholder={`Step ${idx + 1}…`}
                      onChange={(e) => {
                        const next = [...procedureSteps];
                        next[idx] = e.target.value;
                        setProcedureSteps(next);
                      }}
                    />
                    <button
                      onClick={() => setProcedureSteps((prev) => prev.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: '6px 4px', flexShrink: 0 }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setProcedureSteps((prev) => [...prev, ''])}
                  style={{ background: 'none', border: 'none', color: 'var(--electric)', fontSize: 12, cursor: 'pointer', padding: '2px 0', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12 }}
                >
                  <Plus size={13} /> Add Step
                </button>
                {saveProceduresMutation.isError && (
                  <p style={{ color: 'var(--red)', fontSize: 12, margin: '0 0 8px' }}>Failed to save. Try again.</p>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => setEditingProcedures(false)}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    disabled={saveProceduresMutation.isPending}
                    onClick={() => saveProceduresMutation.mutate()}
                  >
                    {saveProceduresMutation.isPending ? 'Saving…' : 'Save Procedures'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                border: hasSteps ? '1px solid var(--amber)' : '1px dashed var(--amber)',
                borderRadius: 8,
                background: 'var(--amber-50)',
                overflow: 'hidden',
              }}>
                {hasSteps ? (
                  <>
                    <div style={{ display: 'flex', borderBottom: '1px solid rgba(245,158,11,0.3)', padding: '6px 12px' }}>
                      <div style={{ width: 60, textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--amber-text)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Number</div>
                      <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'var(--amber-text)', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: 12 }}>Contact Procedure</div>
                    </div>
                    {steps.map((s) => (
                      <div
                        key={s.step}
                        style={{ display: 'flex', borderBottom: '0.5px solid rgba(245,158,11,0.3)', padding: '10px 12px', alignItems: 'flex-start' }}
                      >
                        <div style={{ width: 60, textAlign: 'center', fontWeight: 700, color: 'var(--electric)', fontSize: 14, flexShrink: 0 }}>
                          {s.step}
                        </div>
                        <div style={{ flex: 1, fontSize: 14, color: 'var(--amber-text)', paddingLeft: 12, lineHeight: 1.5 }}>
                          {s.procedure}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div style={{ padding: 24, textAlign: 'center', fontSize: 14, color: 'var(--amber-text)' }}>
                    {canEdit
                      ? 'No contact procedures defined. Click "Edit Procedures" to add steps.'
                      : 'No contact procedures defined.'}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Contacts */}
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          {showArchived ? 'Archived Contacts' : 'Contacts'}{contactTotal > 0 && ` (${contactTotal.toLocaleString()})`}
        </h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', color: 'var(--gray-600)' }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => { setShowArchived(e.target.checked); setContactOffset(0); }}
          />
          Show archived
        </label>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Name</th>
              <th>Title</th>
              <th>Email</th>
              {!isMobile && <th>Phone</th>}
              <th>Type</th>
              <th>Verified</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 ? (
              <tr>
                <td colSpan={isMobile ? 5 : 6} style={{ textAlign: 'center', padding: 24, color: 'var(--gray-500)' }}>
                  {showArchived ? 'No archived contacts' : 'No contacts'}
                </td>
              </tr>
            ) : (
              contacts.map((c) => {
                const preferredPhone = c.phones?.find((p) => p.is_preferred)?.phone_number
                  ?? c.preferred_phones?.[0]
                  ?? null;
                const displayName = c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
                const isArchived = !c.is_active;
                const verif = (() => {
                  if (!(c as any).last_verified_at) return { label: 'Never', color: 'var(--gray-500)', bg: 'var(--gray-100)' };
                  const now = new Date(); now.setHours(0,0,0,0);
                  const then = new Date((c as any).last_verified_at); then.setHours(0,0,0,0);
                  const days = Math.floor((now.getTime() - then.getTime()) / 86400000);
                  if (days <= agingDays) return { label: 'Verified', color: 'var(--green)', bg: 'var(--green-50)' };
                  if (days <= overdueDays) return { label: 'Aging', color: 'var(--amber-text)', bg: 'var(--amber-50)' };
                  return { label: 'Overdue', color: 'var(--hazard-deep)', bg: 'var(--hazard-50)' };
                })();
                return (
                  <tr key={c.id} style={{ cursor: 'pointer', opacity: isArchived ? 0.65 : 1 }} onClick={() => navigate(`/contacts/${c.id}`)}>
                    <td style={{ fontWeight: 500 }}>
                      {displayName}
                      {isArchived && (
                        <span style={{ marginLeft: 6, fontSize: 12, background: 'var(--amber-50)', color: 'var(--amber-text)', borderRadius: 10, padding: '1px 6px', fontWeight: 600 }}>
                          Archived
                        </span>
                      )}
                    </td>
                    <td style={{ color: 'var(--gray-600)', fontSize: 14 }}>{c.title ?? '—'}</td>
                    <td style={{ fontSize: 14 }}>{c.email_primary ?? '—'}</td>
                    {!isMobile && <td style={{ fontSize: 14 }}>{preferredPhone ?? '—'}</td>}
                    <td>
                      <span className={`pill ${c.classification_id === 2 ? 'pill-restricted' : 'pill-standard'}`}>
                        {c.classification_id === 2 ? 'Restricted' : 'Standard'}
                      </span>
                    </td>
                    <td>
                      <span style={{ background: verif.bg, color: verif.color, fontSize: 12, fontWeight: 600, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                        {verif.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!showArchived && contactTotal > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 12 }}>
          <button
            className="btn btn-secondary"
            disabled={contactOffset === 0}
            onClick={() => setContactOffset(Math.max(0, contactOffset - PAGE_SIZE))}
          >
            <ChevronLeft size={14} />
            Prev
          </button>
          <span style={{ fontSize: 14 }}>Page {contactPage} of {contactPages}</span>
          <button
            className="btn btn-secondary"
            disabled={contactOffset + PAGE_SIZE >= contactTotal}
            onClick={() => setContactOffset(contactOffset + PAGE_SIZE)}
          >
            Next
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowEdit(false); }}>
          <div className="modal" style={{ maxWidth: 540 }} onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Edit Agency</h3>
              <button className="btn btn-ghost" onClick={() => setShowEdit(false)}><X size={16} /></button>
            </div>
            {(
              [
                ['name', 'Name', 'text'],
                ['acronym', 'Acronym', 'text'],
                ['street', 'Street', 'text'],
                ['city', 'City', 'text'],
                ['state', 'State', 'text'],
                ['postal_code', 'Postal Code', 'text'],
                ['website', 'Website', 'url'],
              ] as [keyof Agency, string, string][]
            ).map(([field, label, type]) => (
              <div key={field} style={{ marginBottom: 12 }}>
                <label className="field-label">{label}</label>
                <input
                  type={type}
                  className="field-input"
                  value={(editForm[field] as string) ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value || null })}
                />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={updateMutation.isPending}
                onClick={() => updateMutation.mutate()}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
            {updateMutation.isError && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>Failed to save. Try again.</p>
            )}
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowArchiveConfirm(false); }}>
          <div className="modal" style={{ maxWidth: 440 }} onMouseDown={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Archive {agency.name}?</h3>
            <p style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 8 }}>
              This will archive this agency and all{' '}
              <strong>{agency.contact_count ?? 0} contacts</strong> associated with it.
              Archived agencies and contacts will not appear in search results or lists.
            </p>
            <p style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 20 }}>
              This action can be reversed by an Admin.
            </p>
            {archiveMutation.isError && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>Failed to archive. Try again.</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowArchiveConfirm(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--red)', border: 'none' }}
                disabled={archiveMutation.isPending}
                onClick={() => archiveMutation.mutate()}
              >
                {archiveMutation.isPending ? 'Archiving…' : 'Archive Agency'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Confirmation Modal */}
      {showAddContact && (
        <AddContactModal
          onClose={() => { setShowAddContact(false); qc.invalidateQueries({ queryKey: ['agency-contacts', id] }); }}
          defaultAgencyId={id}
        />
      )}

      {showAssociateContact && agency && (
        <AssociateContactModal
          agencyId={id!}
          agencyName={agency.name}
          onClose={() => setShowAssociateContact(false)}
        />
      )}

      {showReactivateConfirm && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowReactivateConfirm(false); }}>
          <div className="modal" style={{ maxWidth: 440 }} onMouseDown={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Reactivate {agency.name}?</h3>
            <p style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 20 }}>
              This will reactivate this agency and all contacts that were archived with it.
              They will appear in search results and lists again.
            </p>
            {reactivateMutation.isError && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>Failed to reactivate. Try again.</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowReactivateConfirm(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={reactivateMutation.isPending}
                onClick={() => reactivateMutation.mutate()}
              >
                {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate Agency'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyDetailPage;
